import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { EditableDashboardListItem } from '@modules/dashboard/types';
import {
  DeleteAnalysisInput,
  GetDashboardByTokenDocument,
  ListAnalysesDocument,
  useDeleteAnalysisMutation,
} from 'generated/graphql';
import { DocumentNode } from 'graphql';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';

const CREATE_ANALYSIS_REFETCH_QUERIES = [ListAnalysesDocument];

interface DeleteAnalysisOptions {
  input: DeleteAnalysisInput;
  dashboardToAddTo?: Pick<
    EditableDashboardListItem,
    'id' | 'viewToken' | 'editToken' | 'title'
  >;
  refetchQueries?: DocumentNode[];
}

export const useDeleteAnalysis = ({
  input,
  dashboardToAddTo,
  refetchQueries,
}: DeleteAnalysisOptions) => {
  const { id: userID } = useGetUser();
  const { showErrorNotification } = useNotificationContext();

  const [
    deleteAnalysis,
    { loading, error: deleteAnalysisMutationError, called },
  ] = useDeleteAnalysisMutation({
    variables: {
      request: input,
    },
    refetchQueries: [
      ...CREATE_ANALYSIS_REFETCH_QUERIES,
      ...(refetchQueries ?? []),
      ...(dashboardToAddTo
        ? [
            {
              query: GetDashboardByTokenDocument,
              variables: {
                token: dashboardToAddTo.viewToken,
                tenantID: input.tenantID,
                userID,
                includeScheduleOutbounds: true,
              },
            },
          ]
        : []),
    ],
    awaitRefetchQueries: Boolean(dashboardToAddTo),
    onError: () => {
      showErrorNotification({
        contentComponent: `Something went wrong.`,
        props: { isClosable: true },
      });
    },
  });

  const status = apolloStatusToQueryStatus(
    loading,
    called,
    false,
    deleteAnalysisMutationError,
  );

  return {
    isLoading: IsLoading(status),
    deleteAnalysis,
  };
};
